/**
 * `useSaveProgressNoteDraft` — force a save-draft of a `SoapNoteDto` against an
 * encounter-scoped Progress Note.
 *
 * Gate-clinical-writes-until-Lock: `POST /visits/:encounterId/soap-note` is now
 * an idempotent SAVE-DRAFT. It persists ONLY the draft (QuestionnaireResponse
 * snapshot + a single preliminary DocumentReference, find-or-update per
 * encounter) and writes NO clinical resources. Safe to call repeatedly — used
 * by:
 *   - `<ProgressNoteEdit>` debounced auto-save + explicit Save
 *   - `<ProgressNoteViewer>`'s Lock force-save (the BLOCK-on-fail step before
 *     `/lock` materializes the clinical resources)
 *
 * Resolves the `VisitContextDto` (practitioner / project / organization) the
 * middleware requires from the active Medplum profile, mirroring the logic in
 * `<ProgressNoteEdit>.handleSubmit`. Returns the `CreateSoapNoteResponseDto`
 * (notably `documentReferenceId`, which is stable across saves — the caller
 * threads it straight into the subsequent `/lock` call).
 */
import { useCallback } from 'react'
import { useMedplum } from '@medplum/react'
import type { OrpcClient } from '@/orpc/client'
import { useClient } from '@/hooks/useClient'
import type { SoapNoteDto } from '@/pages/Patient/components/ProgressNoteEdit/draftToSoap'

type CreateSoapNoteResponse = Awaited<ReturnType<OrpcClient['visitSaveSoapNoteDraft']>>
type VisitContextDto = Parameters<OrpcClient['visitSaveSoapNoteDraft']>[0]['visitContext']
type SoapNoteInput = Parameters<OrpcClient['visitSaveSoapNoteDraft']>[0]['soapNote']

export type SaveProgressNoteDraftInput = {
  encounterId: string
  patientId: string
  soapNote: SoapNoteDto
  /** Leave billing out so the server keeps the Claim: the form's copy was incomplete as loaded. */
  withholdBilling?: boolean
  /** Forwarded so the BE can attribute the AI-scribe Provenance agent. Omit
   *  when no live scribe session (manual edit-mode save / viewer lock). */
  scribeSessionId?: string | null
  /** The note being edited, once it exists — a save to a note removed from the chart is refused. */
  documentReferenceId?: string
  /** Optional overrides for the workspace flows that already know the ids. */
  practitionerId?: string
  organizationId?: string
  projectId?: string
}

export function useSaveProgressNoteDraft() {
  const medplum = useMedplum()
  const client = useClient()

  return useCallback(
    async ({
      encounterId,
      patientId,
      soapNote,
      withholdBilling,
      scribeSessionId,
      documentReferenceId,
      practitionerId,
      organizationId,
      projectId,
    }: SaveProgressNoteDraftInput): Promise<CreateSoapNoteResponse> => {
      const profile = medplum.getProfile()
      if (!profile?.id) {
        throw new Error('Practitioner profile is required to save a progress note.')
      }
      const resolvedProjectId = projectId ?? medplum.getProject()?.id
      if (!resolvedProjectId) {
        throw new Error('Project not found')
      }

      let resolvedOrganizationId = organizationId
      if (!resolvedOrganizationId) {
        const practitionerRole = await medplum.searchOne(
          'PractitionerRole',
          `practitioner=Practitioner/${profile.id}`,
        )
        resolvedOrganizationId = practitionerRole?.organization?.reference?.split('/')[1]
      }
      if (!resolvedOrganizationId) {
        throw new Error('Organization not found for practitioner')
      }

      const visitContext: VisitContextDto = {
        patientId,
        practitionerId: practitionerId ?? profile.id,
        projectId: resolvedProjectId,
        organizationId: resolvedOrganizationId,
        ...(scribeSessionId ? { scribeSessionId } : {}),
      }

      return client.visitSaveSoapNoteDraft({
        encounterId,
        // draftToSoap yields the legacy `SoapNoteDto` (billingItems.dx optional);
        // the oRPC contract requires `dx`, which draftToSoap always populates.
        soapNote: (withholdBilling
          ? { ...soapNote, billing: undefined }
          : soapNote) as SoapNoteInput,
        visitContext,
        ...(documentReferenceId ? { documentReferenceId } : {}),
      })
    },
    [medplum, client],
  )
}
